import React, { useState, useRef, useCallback } from "react";
import { useIntl } from "react-intl";
import { FolderArrowDownIcon } from "@heroicons/react/24/outline"; // adapte si besoin
import { uploadSingleFile } from "../../../utils/cloudinary"; // adapte le chemin

type CoverUploaderProps = {
  onUploaded: (url: string) => void;
};

export function CoverUploader({ onUploaded }: CoverUploaderProps) {
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState<number | null>(null); // null = pas d'upload en cours
  const isUploading = progress !== null;

  const upload = useCallback(
    async (file: File) => {
      // Une cover = une image, et Instagram plafonne à 8 Mo
      if (!file.type.startsWith("image/")) {
        alert("La cover doit être une image (JPG, PNG, WEBP…).");
        return;
      }
      if (file.size > 8_000_000) {
        alert("La cover doit faire moins de 8 Mo (limite Instagram).");
        return;
      }

      setProgress(0);
      try {
        const secureUrl = await uploadSingleFile(file, setProgress);
        // Instagram exige un JPEG → on force le format à la livraison Cloudinary
        const jpgUrl = secureUrl.replace("/upload/", "/upload/f_jpg/");
        onUploaded(jpgUrl);
      } catch (err) {
        console.error("Upload de la cover échoué :", err);
        alert("L'upload de la cover a échoué, réessaie.");
      } finally {
        setProgress(null);
      }
    },
    [onUploaded]
  );

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
    e.target.value = ""; // permet de re-sélectionner le même fichier
  };

  // --- drag & drop (porté du contrôleur Stimulus) ---
  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current += 1;
    if (!isUploading) setIsDragging(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setIsDragging(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && !isUploading) upload(file);
  };

  return (
    <div
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className={`relative flex flex-col items-center justify-center py-3 px-3 md:px-5 md:py-5 border-2 border-dashed rounded-md transition-colors duration-200 ease-in-out ${isDragging ? "border-brand_500 bg-brand_50" : "border-gray-300"
        }`}
    >
      <div className="flex items-center gap-3">
        <FolderArrowDownIcon className="size-6 text-gray-400" />
        <div className="flex flex-col justify-center">
          <label className="relative flex gap-1 cursor-pointer">
            <span className="clickable-text underline">
              {intl.formatMessage({ id: "uploader.browse", defaultMessage: "Parcourir" })}
            </span>
            <span className="whitespace-nowrap hidden md:block">
              {intl.formatMessage({ id: "uploader.or_drag_drop", defaultMessage: "ou glisser-déposer" })}
            </span>
            <input
              ref={inputRef}
              type="file"
              className="sr-only"
              accept="image/png,image/jpg,image/jpeg,image/bmp,image/gif,image/webp"
              onChange={onChange}
            />
          </label>
          <p className="text-xs text-gray-500">PNG, JPG, JPEG, WEBP — jusqu'à 8 Mo</p>
        </div>
      </div>

      {/* Barre de progression, même style que l'uploader existant */}
      <div className="absolute w-full left-1 bottom-1 h-2 px-2">
        <div
          className={`transition-[width] duration-500 h-2 animate-pulse rounded-md bg-brand_500 ${isUploading ? "" : "hidden"
            }`}
          style={{ width: `${progress ?? 0}%` }}
        />
      </div>
    </div>
  );
}
